import { useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { User } from "@supabase/supabase-js";

interface ProtectedAdminRouteProps {
  children: React.ReactNode;
}

export default function ProtectedAdminRoute({ children }: ProtectedAdminRouteProps) {
  const [user, setUser] = useState<User | null>(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let mounted = true;
    let timeoutId: NodeJS.Timeout;
    let hasResolved = false;

    timeoutId = setTimeout(() => {
      // Only trigger timeout if RPC hasn't resolved yet
      if (mounted && loading && !hasResolved) {
        console.log('🔐 ProtectedAdminRoute: Timeout reached - denying access');
        setIsAdmin(false);  // Deny by default on timeout
        setLoading(false);
      }
    }, 5000); // Increased to 5 seconds

    const checkAdminStatus = async () => {
      try {
        console.log('🔐 ProtectedAdminRoute: Checking admin status');
        
        // Use SECURITY DEFINER function - no user_id needed, uses auth.uid() internally
        const { data: isAdminUser, error } = await supabase
          .rpc('is_admin_authenticated');
        
        console.log('🔐 ProtectedAdminRoute: Admin check result:', { 
          isAdmin: isAdminUser,
          error: error?.message 
        });
        
        if (mounted) {
          hasResolved = true;  // Mark as resolved
          clearTimeout(timeoutId);  // Clear timeout immediately
          
          if (error) {
            console.error('🔐 ProtectedAdminRoute: RPC error:', error.message);
            setIsAdmin(false);
            setLoading(false);
            return;
          }
          
          console.log('🔐 ProtectedAdminRoute: Final decision - Access allowed:', isAdminUser);
          setIsAdmin(!!isAdminUser);
          setLoading(false);
        }
      } catch (e) {
        console.error('🔐 ProtectedAdminRoute: Unexpected error:', e);
        if (mounted) {
          hasResolved = true;
          clearTimeout(timeoutId);
          setIsAdmin(false);
          setLoading(false);
        }
      }
    };

    const initializeAuth = async () => {
      try {
        // First get current session
        const { data: { session }, error: sessionError } = await supabase.auth.getSession();
        console.log('🔐 ProtectedAdminRoute: Initial session check:', session?.user?.id, sessionError);
        
        if (mounted) {
          const currentUser = session?.user ?? null;
          setUser(currentUser);

          if (currentUser) {
            await checkAdminStatus();
          } else {
            setIsAdmin(false);
            setLoading(false);
          }
        }
      } catch (error) {
        console.error('🔐 ProtectedAdminRoute: Session check error:', error);
        if (mounted) {
          setUser(null);
          setIsAdmin(false);
          setLoading(false);
        }
      }
    };

    // Set up auth state listener
    const { data: { subscription } } = supabase.auth.onAuthStateChange(async (event, session) => {
      console.log('🔐 ProtectedAdminRoute: Auth state change:', event, session?.user?.id);
      
      if (!mounted) return;
      
      const currentUser = session?.user ?? null;
      setUser(currentUser);

      if (currentUser) {
        await checkAdminStatus();
      } else {
        setIsAdmin(false);
        setLoading(false);
      }
    });

    // Initialize
    initializeAuth();

    return () => {
      mounted = false;
      if (timeoutId) clearTimeout(timeoutId);
      subscription.unsubscribe();
    };
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-center space-y-4">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto"></div>
          <p className="text-muted-foreground">กำลังตรวจสอบสิทธิ์...</p>
        </div>
      </div>
    );
  }

  console.log('🔐 ProtectedAdminRoute: Final check - User:', !!user, 'IsAdmin:', isAdmin);

  if (!user) {
    console.log('🔐 ProtectedAdminRoute: No user - redirecting to login');
    return <Navigate to="/admin/login" replace />;
  }
  
  if (!isAdmin) {
    console.log('🔐 ProtectedAdminRoute: User not admin - redirecting to login');
    return <Navigate to="/admin/login" replace />;
  }

  return <>{children}</>;
}